import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { SwipeableViewsWithDots } from "@src/appV2/redesign/components/SwipeableViewsWithDots";
import { useCallback, useState } from "react";

import { ONBOARDING_STAGES } from "../../util/constants";
import { EducationIntroSlide } from "../components/EducationIntroSlide";
import { type OnboardingStepProps } from "../OnboardingStepWrapper";
import { InstantInterviewSlide } from "./components/InstantInterviewSlide";
import { JobCardsSlide } from "./components/JobCardsSlide";
import { QualificationApprovedSlide } from "./components/QualificationApprovedSlide";

const SLIDE_IDS = ["intro", "verify-credentials", "instant-interview", "upload-documents"] as const;
type SlideId = (typeof SLIDE_IDS)[number];

function SlideContent(props: { slideId: SlideId }) {
  switch (props.slideId) {
    case "intro":
      return <EducationIntroSlide marketplaceType="jobs" />;
    case "verify-credentials":
      return <JobCardsSlide />;
    case "instant-interview":
      return <InstantInterviewSlide />;
    case "upload-documents":
      return <QualificationApprovedSlide />;
  }
}

export function JobsEducationStepContent(props: OnboardingStepProps) {
  const { onNext } = props;
  const [currentIndex, setCurrentIndex] = useState(0);

  const isLastSlide = currentIndex === SLIDE_IDS.length - 1;

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_JOBS_EDUCATION_PAGE_VIEWED);

  const handleContinue = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
      stepCompleted: ONBOARDING_STAGES.JOBS_EDUCATION,
      slideIndex: currentIndex,
    });
    void onNext();
  }, [currentIndex, onNext]);

  const handleChangeStep = useCallback((step: number) => {
    setCurrentIndex(step);
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((previous) => Math.min(previous + 1, SLIDE_IDS.length - 1));
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SwipeableViewsWithDots
        activeStep={currentIndex}
        dotsPosition="bottom"
        dotsOffset="20%"
        onChangeStep={handleChangeStep}
      >
        {SLIDE_IDS.map((slideId) => (
          <SlideContent key={slideId} slideId={slideId} />
        ))}
      </SwipeableViewsWithDots>

      <PageFooterWrapper>
        {isLastSlide ? (
          <Button fullWidth size="large" variant="contained" onClick={handleContinue}>
            Get started
          </Button>
        ) : (
          <Button fullWidth size="large" variant="outlined" onClick={handleNext}>
            Next
          </Button>
        )}
      </PageFooterWrapper>
    </Box>
  );
}
